/**
 * review-list.js - enhancement for the native review section.
 *
 * Everything this file does is optional. The section already contains the
 * rating, the histogram, the photo wall and twelve real reviews as server
 * rendered HTML; this adds the carousel controls, the lightbox, expanding a
 * truncated review, and loading the rest of the reviews from the Railway API.
 *
 * With JavaScript off, none of the controls below ever appear: they ship with
 * the `hidden` attribute and are revealed here.
 */

(() => {
  'use strict';

  const PAGE = 200;       // the read endpoint's ceiling per request
  const WALL_STEP = 12;   // photo tiles appended per "load more", matching the first page

  const esc = (v) => String(v ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  /** "Kimberly Robinson" -> "Kimberly R.", matching what the server writes. */
  function shortName(name) {
    const t = String(name ?? '').trim();
    if (!t) return 'Anonymous';
    // An email address is not a display name, and shortening it still
    // exposes the handle. Mirrors the rule the server applies.
    if (/\S+@\S+\.\S+/.test(t)) return 'Anonymous';
    const parts = t.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 40);
    return `${parts.slice(0, -1).join(' ').slice(0, 30)} ${parts[parts.length - 1][0].toUpperCase()}.`;
  }

  const fmtDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  /**
   * The review a photo strip tile carries, written into data attributes by the
   * section so no request is needed to open it.
   *
   * Returns null when the attributes are absent, which is what an older
   * metafield looks like before the next sync runs. That degrades to the
   * photo-only lightbox rather than opening an empty panel.
   */
  function metaFromTile(el) {
    const d = el.dataset;
    const body = d.rvB || '';
    if (!body && !d.rvT) return null;
    return {
      name: d.by || 'Anonymous',
      title: d.rvT || '',
      // Bodies over 600 characters are clipped server side. Say so rather than
      // stopping mid sentence and passing it off as the end of the review.
      body: d.rvCut === '1' ? `${body}\u2026` : body,
      rating: d.rvR || '',
      when: fmtDate(d.rvD),
      verified: d.rvVb === '1',
    };
  }

  /** One strip tile, matching what the section renders for the first page. */
  function wallTileHTML(p, index, productTitle = '') {
    const rv = p.rv || {};
    return `<button class="pw__i" type="button" data-wall-open="${index}"
      data-rid="${esc(p.rid)}" data-by="${esc(p.by)}"
      data-rv-r="${esc(rv.r)}" data-rv-d="${esc(rv.d)}"
      data-rv-t="${esc(rv.t)}" data-rv-b="${esc(rv.b)}"
      ${rv.cut ? 'data-rv-cut="1"' : ''}${rv.vb ? ' data-rv-vb="1"' : ''}
      aria-label="Read the review by ${esc(p.by)}, with photo">
      <img src="${esc(p.t || p.u)}" data-fullsrc="${esc(p.u)}"
           alt="${esc(mediaAlt(productTitle, p.by, 'photo'))}" width="400" height="400"
           decoding="async" loading="lazy"></button>`;
  }

  /**
   * The same sentence the server writes for the same image.
   *
   * These sit inside buttons that carry their own aria-label, so this is not
   * the accessible name. It is what a reader sees when the image does not
   * arrive, and review media is served from the app rather than the Shopify
   * CDN, so that happens. A card whose photo fails should still say what the
   * photo was of, exactly as the server-rendered cards do.
   *
   * The author is optional: an anonymous review would otherwise end on a
   * dangling "by".
   */
  function mediaAlt(productTitle, by, kind) {
    const what = kind === 'video' ? 'Customer review video' : 'Customer review photo';
    const of = productTitle ? ` of ${productTitle}` : '';
    const who = by ? ` by ${by}` : '';
    return `${what}${of}${who}`;
  }

  const SHIELD = '<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" focusable="false"><path d="M12 2L4 5.5V11.5C4 16 7.5 20 12 21.5C16.5 20 20 16 20 11.5V5.5L12 2Z" fill="#22c55e"/><polyline points="8.5,12 11,14.5 15.5,9.5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';

  class ReviewSection {
    constructor(root) {
      this.root = root;
      this.endpoint = root.dataset.endpoint;
      this.productId = root.dataset.productId;
      this.total = Number(root.dataset.total) || 0;
      this.lbToken = 0;
      // Whether anything is still hidden. Decided by syncMore from real counts
      // before the first paint; never assumed. It used to start true, which is
      // what put a fade over a complete set of two reviews.
      this.hasMore = false;

      // Narrowing the list can need a request. Each one takes a ticket, and a
      // response whose ticket is no longer the current one is discarded, so a
      // slow answer for a chip the reader has already moved on from can never
      // repaint the list underneath them.
      this.reqSeq = 0;
      this.pending = null;     // AbortController for the request in flight
      this.loadError = null;   // set while the list cannot be trusted

      // Used for the alt text on media this script renders, so a card loaded
      // by Load more says the same thing as one the server wrote.
      this.productTitle = root.dataset.productTitle || '';

      this.grid = root.querySelector('[data-grid]');
      this.gridwrap = root.querySelector('[data-gridwrap]');
      this.moreBtn = root.querySelector('[data-more]');
      this.wall = root.querySelector('[data-wall]');
      this.lb = root.querySelector('[data-lightbox]');
      this.lbPanel = root.querySelector('[data-lb-panel]');

      this.all = null;          // full review set, once fetched
      this.serverShown = Number(root.dataset.shown) || 12;
      this.shown = this.initialShown();   // how many are on screen
      this.step = 12;           // how many more each Load more adds
      this.filter = 'all';
      this.topic = null;
      this.sort = 'newest';
      this.query = '';
      this.busy = false;

      this.reveal();
      this.bind();
      this.captureOrder();
      // Clearing a filter restores these rather than refetching, so it can
      // never fail. Taken before the first layout, which only moves them.
      this.serverCards = this.cardOrder.slice();
      this.layout();
      // Before the first clamp, not after it: applyPeek reads hasMore, and
      // until this ran nothing had ever measured whether there was more.
      this.syncMore(this.total);
      this.applyPeek();
      this.watchHeights();
      this.prefetchWhenNear();
      this.syncWallNav();

      // Column count changes with width, so the deal has to be redone.
      let t;
      window.addEventListener('resize', () => {
        clearTimeout(t);
        t = setTimeout(() => {
          if (!this.expanded) this.shown = this.initialShown();
          this.layout();
          this.applyPeek();
          this.syncWallNav();
        }, 150);
      });
    }

    /**
     * Card heights move under us: review photos land after the first measure,
     * and a clamp taken before they loaded is too high, so an expand can add
     * cards without the visible region growing at all. Watching the grid box
     * re-takes the measurement whenever that happens.
     */
    watchHeights() {
      if (!this.grid || typeof ResizeObserver === 'undefined') return;
      let t;
      const ro = new ResizeObserver(() => {
        clearTimeout(t);
        t = setTimeout(() => this.applyPeek(), 60);
      });
      ro.observe(this.grid);
      this.heightWatch = ro;
    }

    /**
     * Warm the review cache once the section is within a screen of the
     * viewport, so the first Load more returns at once rather than after a
     * round trip. Quiet: if it fails the click just fetches as before.
     */
    prefetchWhenNear() {
      if (typeof IntersectionObserver === 'undefined') return;
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        const warm = () => { this.ensureAll().catch(() => {}); };
        if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 3000 });
        else setTimeout(warm, 600);
      }, { rootMargin: '100% 0px' });
      io.observe(this.root);
    }

    /**
     * The first run of cards before the fade. One narrow column makes twelve
     * cards an enormous amount of scrolling before the reader reaches the
     * button, so the phone starts at five. Desktop is untouched.
     */
    initialShown() {
      return this.columnCount() === 1 ? 5 : this.serverShown;
    }

    /** How many columns the current width should show. Matches the CSS. */
    columnCount() {
      const w = window.innerWidth;
      return w <= 860 ? 1 : w <= 1100 ? 2 : 3;
    }

    /**
     * Deals the cards round robin into column elements: 1,2,3 across the
     * first row, then 4,5,6, and so on. That keeps the reading order a grid
     * gives while letting each column pack tight, which a grid cannot.
     */
    /**
     * Remembers the order the cards were rendered in.
     *
     * layout() re-parents cards into column elements, so after it has run
     * once the DOM is column major and reading it back gives 1,4,7,2,5,8.
     * Dealing from that produces nonsense at the next width, which is exactly
     * what happened at two columns. The original order is captured whenever
     * the grid is written flat, and every deal works from it.
     */
    /**
     * The fade is not a one-off on the first expand. It sits at the bottom of
     * whatever is currently shown, for as long as there are more reviews to
     * come, and it is what stops the grid ending in a ragged edge.
     *
     * Cards vary in height, so dealing them round-robin into columns leaves
     * the columns finishing at different heights. Clamping at the SHORTEST
     * column cuts every column on the same line and puts the gradient over
     * that line, so there is no white space under the short columns. The CSS
     * max-height stays as the no-JS fallback only.
     */
    applyPeek() {
      const wrap = this.gridwrap;
      if (!wrap) return;
      if (!this.hasMore) {
        wrap.classList.remove('is-clamped');
        wrap.style.maxHeight = '';
        return;
      }
      wrap.classList.add('is-clamped');
      const top = wrap.getBoundingClientRect().top;
      const cols = [...wrap.querySelectorAll('.grid__col')];
      let cut;
      if (cols.length > 1) {
        cut = Math.min(...cols.map(c => c.getBoundingClientRect().bottom - top));
      } else {
        const cards = this.cardOrder || [];
        const n = Math.min(this.shown, cards.length);
        cut = n ? cards[n - 1].getBoundingClientRect().bottom - top : 0;
      }
      // Never clamp so tightly that the fade eats the whole grid.
      const anchor = this.readingAnchor();
      wrap.style.maxHeight = `${Math.round(Math.max(360, cut))}px`;
      this.restoreAnchor(anchor);
    }

    /**
     * Anchor to the review being read, not to the button.
     *
     * Expanding grows the grid, and everything below it moves down. Left to
     * itself the page slides the reader down with the button, so the cards
     * they were part way through scroll out of view and they have to hunt for
     * their place again. The first card still on screen is pinned to the
     * exact pixel it was at, so clicking Load more leaves the reader looking
     * at the same reviews and the new ones arrive below, to be scrolled to
     * when they choose.
     *
     * Only once the reader has expanded something: during the initial load,
     * images settling are ordinary page behaviour and must not move anything.
     */
    readingAnchor() {
      if (!this.expanded) return null;
      for (const c of (this.cardOrder || [])) {
        const r = c.getBoundingClientRect();
        if (r.bottom > 0) return { el: c, top: r.top };
      }
      return null;
    }

    restoreAnchor(a) {
      if (!a || !a.el.isConnected) return;
      const drift = a.el.getBoundingClientRect().top - a.top;
      if (Math.abs(drift) > 1 && Math.abs(drift) < 20000) {
        window.scrollBy({ top: drift, behavior: 'instant' });
      }
    }

    captureOrder() {
      // A product with no reviews of its own renders no [data-grid], so this
      // is null there. layout() already guarded for that; this did not, so the
      // constructor threw on every zero-review page. Roughly 300 of 413
      // products, and the throw took the rest of init with it.
      if (!this.grid) { this.cardOrder = []; return; }
      this.cardOrder = [...this.grid.querySelectorAll('.rc')];
    }

    layout() {
      if (!this.grid) return;
      const cards = (this.cardOrder || []).filter(c => c.isConnected || true);
      if (!cards.length) { this.grid.classList.remove('grid--masonry'); return; }

      const cols = this.columnCount();
      if (cols === 1) {
        this.grid.classList.remove('grid--masonry');
        this.grid.replaceChildren(...cards);
        return;
      }

      const buckets = Array.from({ length: cols }, () => {
        const d = document.createElement('div');
        d.className = 'grid__col';
        return d;
      });
      cards.forEach((card, i) => buckets[i % cols].appendChild(card));
      this.grid.classList.add('grid--masonry');
      this.grid.replaceChildren(...buckets);
    }

    /** Controls ship hidden so a no-script visitor never sees a dead button. */
    reveal() {
      for (const el of this.root.querySelectorAll('[hidden]')) {
        if (el.hasAttribute('data-lightbox')) continue;
        // syncMore is the only thing that decides whether Load more is shown,
        // so revealing it here would flash a button that does not apply.
        if (el.hasAttribute('data-more-wrap')) continue;
        el.hidden = false;
      }
    }

    bind() {
      this.root.addEventListener('click', (e) => {
        const wallOpen = e.target.closest('[data-wall-open]');
        if (wallOpen) return this.openWall(wallOpen);
        const wallMore = e.target.closest('[data-wall-more]');
        if (wallMore) return this.loadMoreWall(wallMore);
        if (e.target.closest('[data-lb-prev]')) return this.stepLightbox(-1);
        if (e.target.closest('[data-lb-next]')) return this.stepLightbox(1);
        if (e.target.closest('[data-wall-prev]')) return this.scrollWall(-1);
        if (e.target.closest('[data-wall-next]')) return this.scrollWall(1);

        const shot = e.target.closest('[data-open]');
        if (shot) return this.openReview(shot.dataset.open, Number(shot.dataset.idx) || 0);

        const full = e.target.closest('[data-full]');
        if (full) return this.expand(full);

        if (e.target.closest('[data-lb-close]') || e.target === this.lb) return this.closeLightbox();

        const star = e.target.closest('[data-star]');
        if (star) return this.toggleStar(star);

        const chip = e.target.closest('[data-filter]');
        if (chip) return this.setFilter(chip);

        const topic = e.target.closest('[data-topic]');
        if (topic) return this.toggleTopic(topic);

        const hl = e.target.closest('[data-highlight]');
        if (hl) { e.preventDefault(); return this.openHighlight(hl.dataset.highlight); }

        if (e.target.closest('[data-retry]')) return this.retry();

        if (e.target.closest('[data-more]')) return this.loadMore();
      });

      const sort = this.root.querySelector('[data-sort]');
      if (sort) sort.addEventListener('change', () => { this.sort = sort.value; this.rerender(); });

      const search = this.root.querySelector('[data-search]');
      if (search) {
        let t;
        search.addEventListener('input', () => {
          clearTimeout(t);
          t = setTimeout(() => { this.query = search.value.trim().toLowerCase(); this.shown = 12; this.rerender(); }, 250);
        });
      }

      document.addEventListener('keydown', (e) => {
        if (!this.lb || this.lb.hidden || !this.lb.classList.contains('is-open')) return;
        if (e.key === 'Escape') { e.preventDefault(); this.closeLightbox(); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.stepLightbox(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.stepLightbox(1); }
      });
    }

    // ── data ──────────────────────────────────────────────────────────────

    /** Fetches the whole published set once, then everything works locally. */
    async ensureAll(signal) {
      if (this.all) return this.all;
      const out = [];
      let offset = 0;
      for (;;) {
        const res = await fetch(`${this.endpoint}/reviews?product_id=${encodeURIComponent(this.productId)}&limit=${PAGE}&offset=${offset}`, {
          headers: { Accept: 'application/json' }, signal,
        });
        if (!res.ok) throw new Error(`reviews ${res.status}`);
        const data = await res.json();
        out.push(...(data.reviews || []));
        const total = Number(data.total) || out.length;
        offset += PAGE;
        if (out.length >= total || !(data.reviews || []).length) break;
      }
      this.all = out;
      return out;
    }

    async withBusy(fn) {
      if (this.busy) return;
      this.busy = true;
      if (this.moreBtn) this.moreBtn.disabled = true;
      const note = document.createElement('p');
      note.className = 'rv__busy';
      note.setAttribute('role', 'status');
      note.textContent = 'Loading reviews…';
      this.gridwrap.after(note);
      try {
        await fn();
      } catch (err) {
        // No four second self-dismiss. The reader asked for more reviews and
        // did not get them; the message stays, with a way to ask again, until
        // they act.
        note.remove();
        this.busy = false;
        if (this.moreBtn) this.moreBtn.disabled = false;
        this.renderNotice();
        return;
      }
      note.remove();
      this.busy = false;
      if (this.moreBtn) this.moreBtn.disabled = false;
    }

    // ── list ──────────────────────────────────────────────────────────────

    /**
     * Twelve more, not all of them.
     *
     * Loading the whole set on the first click meant a product with 222
     * reviews dumped 222 cards into the page at once, which is as unhelpful
     * as showing twelve. The data is still fetched in one go, because the
     * endpoint pages at 200 and a second round trip mid-scroll is worse, but
     * only `shown` cards are rendered and the button keeps its place.
     */
    loadMore() {
      this.expanded = true;
      const anchor = this.readingAnchor();
      // Every click brings twelve more reviews.
      //
      // There used to be a free first expand that only lifted the fade off
      // the six extra cards already in the HTML. But the clamp is measured
      // from the shortest column of everything rendered, so those six were
      // already all but visible and the click bought about a third of a card.
      // It read as a dead button, and the reader had to click twice before
      // anything arrived.
      //
      // The click counts immediately, before any fetch, so clicks landing
      // while the first request is in flight are not swallowed by the busy
      // guard.
      // On one column the clamp follows `shown`, so stepping it forward really
      // does reveal more. On the wider grid the clamp follows the shortest
      // column instead, so `shown` has to clear everything already rendered
      // or the click buys nothing.
      this.shown = this.columnCount() === 1
        ? this.shown + this.step
        : Math.max(this.shown, (this.cardOrder || []).length) + this.step;

      // Already on the page: reveal it without a request.
      if (this.shown <= (this.cardOrder || []).length) {
        this.root.dataset.shown = String(this.shown);
        this.syncMore(this.total);
        this.applyPeek();
        this.restoreAnchor(anchor);
        return;
      }
      if (this.all) { this.appendMore(this.currentList()); this.restoreAnchor(anchor); return; }
      return this.withBusy(async () => {
        await this.ensureAll();
        this.appendMore(this.currentList());
        this.restoreAnchor(anchor);
      });
    }

    /** Keeps the Load more button honest about whether anything is left. */
    /**
     * The single source of truth for "is anything still hidden".
     *
     * Both the Load more button and the fade read this, so they cannot
     * disagree. `matching` is the size of the set currently being shown from:
     * the product's whole published count when unfiltered, the size of the
     * filtered result when a chip or search is active.
     *
     * hasMore is set BEFORE the early return. The section omits the .more
     * wrapper entirely when the server already knows there is no page two, so
     * returning first left hasMore at its old value and the fade stayed up
     * over a complete set.
     */
    syncMore(matching) {
      const left = matching - this.shown;
      this.hasMore = left > 0;
      const wrap = this.moreBtn && this.moreBtn.closest('.more');
      if (!wrap) return;
      if (left <= 0) { wrap.hidden = true; return; }
      wrap.hidden = false;
      const label = this.moreBtn.childNodes[0];
      // Just the invitation, not the arithmetic. The count changed on every
      // press and told a reader nothing they wanted: they are deciding whether
      // to keep reading, not how many are left to read.
      if (label) label.nodeValue = 'Load more reviews ';
    }

    toggleStar(btn) {
      const star = btn.dataset.star;
      const on = btn.getAttribute('aria-pressed') === 'true';
      for (const b of this.root.querySelectorAll('[data-star]')) b.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-pressed', on ? 'false' : 'true');
      this.filter = on ? 'all' : star;
      this.topic = null;
      for (const b of this.root.querySelectorAll('[data-topic]')) b.setAttribute('aria-pressed', 'false');
      this.shown = this.initialShown();
      this.syncChips();
      this.rerender();
    }

    setFilter(chip) {
      for (const c of this.root.querySelectorAll('[data-filter]')) c.classList.remove('is-on');
      chip.classList.add('is-on');
      this.filter = chip.dataset.filter;
      this.topic = null;
      for (const b of this.root.querySelectorAll('[data-topic]')) b.setAttribute('aria-pressed', 'false');
      this.shown = this.initialShown();
      for (const b of this.root.querySelectorAll('[data-star]')) {
        b.setAttribute('aria-pressed', b.dataset.star === this.filter ? 'true' : 'false');
      }
      this.rerender();
    }

    /**
     * Filter the list to the reviews a theme actually matched.
     *
     * The pattern comes from the payload, which is the same pattern the count
     * was produced by on the server. That is the whole point: the chip claims
     * a number and clicking it has to be able to prove it. Compiling a second
     * copy of the rule here would work right up until the two drifted.
     */
    toggleTopic(btn) {
      const on = btn.getAttribute('aria-pressed') === 'true';
      for (const b of this.root.querySelectorAll('[data-topic]')) b.setAttribute('aria-pressed', 'false');

      if (on) { this.topic = null; }
      else {
        btn.setAttribute('aria-pressed', 'true');
        let re = null;
        try { re = new RegExp(btn.dataset.topicRe, 'i'); }
        catch { re = null; }   // a pattern we cannot compile filters nothing
        // The rating floor travels with the chip, so the list narrows exactly
        // the way the count was taken. Hard-coding 4 here would be a second
        // copy of a server rule, which is what the pattern already avoids.
        const min = Number(btn.dataset.topicMin) || 0;
        this.topic = re ? { key: btn.dataset.topic, re, min } : null;
        if (!this.topic) btn.setAttribute('aria-pressed', 'false');
      }

      // A theme and a star rating narrow different things; clearing the star
      // keeps the count on the chip matching what the list shows.
      this.filter = 'all';
      for (const b of this.root.querySelectorAll('[data-star]')) b.setAttribute('aria-pressed', 'false');
      this.syncChips();
      this.shown = this.initialShown();
      this.rerender();
    }

    /**
     * Take the reader to the full review a highlight was quoted from.
     *
     * The quote is a fragment of a real review and the review may not be in
     * the twelve on screen, so the whole set is loaded and everything
     * narrowing the list is cleared before jumping, rather than scrolling to
     * an anchor that is not there.
     */
    openHighlight(id) {
      const jump = () => {
        const card = this.root.querySelector(`[data-review="${CSS.escape(String(id))}"]`);
        if (!card) return;
        const full = card.querySelector('[data-full]');
        if (full) this.expand(full);
        card.scrollIntoView({ block: 'center' });
        for (const c of this.root.querySelectorAll('.rc.is-target')) c.classList.remove('is-target');
        card.classList.add('is-target');
        setTimeout(() => card.classList.remove('is-target'), 2600);
      };

      if (this.root.querySelector(`[data-review="${CSS.escape(String(id))}"]`)) return jump();

      return this.withBusy(async () => {
        await this.ensureAll();
        this.topic = null;
        this.filter = 'all';
        this.query = '';
        const search = this.root.querySelector('[data-search]');
        if (search) search.value = '';
        for (const b of this.root.querySelectorAll('[data-topic],[data-star]')) b.setAttribute('aria-pressed', 'false');
        this.syncChips();

        // Show enough of the list to reach it, wherever it sits in the order.
        const list = this.currentList();
        const at = list.findIndex(r => String(r.review_id) === String(id));
        this.shown = at >= 0 ? at + 1 : this.shown;
        this.paint();
        jump();
      });
    }

    syncChips() {
      for (const c of this.root.querySelectorAll('[data-filter]')) {
        c.classList.toggle('is-on', c.dataset.filter === this.filter);
      }
    }

    /** Any narrowing needs the full set, otherwise it would filter 12 of 222. */
    rerender() {
      const needsAll = this.filter !== 'all' || this.topic || this.query || this.sort !== 'newest' || this.expanded;

      // Back to the plain list. The cards the server rendered are exactly that
      // list, so clearing a filter never needs a request and therefore cannot
      // fail or leave a wrong list on screen.
      if (!needsAll) {
        this.cancelPending();
        this.clearError();
        if (this.all) this.paint(); else this.restoreServerCards();
        return;
      }

      if (this.all) { this.cancelPending(); this.clearError(); this.paint(); return; }
      return this.narrow();
    }

    /**
     * Fetch what a narrowing needs, and never let a failure leave the list
     * disagreeing with the controls.
     *
     * The old path showed unfiltered reviews under a chip that still read as
     * selected, and took the error message away after four seconds, so the
     * page ended up quietly claiming a filter it was not applying.
     */
    narrow() {
      const ticket = ++this.reqSeq;
      this.cancelPending();
      const ac = new AbortController();
      this.pending = ac;
      this.showLoading();

      return this.ensureAll(ac.signal).then(
        () => {
          if (ticket !== this.reqSeq) return;   // a newer selection has taken over
          this.pending = null;
          this.clearError();
          this.paint();
        },
        (err) => {
          if (ticket !== this.reqSeq) return;
          if (err && err.name === 'AbortError') return;
          this.pending = null;
          this.showError();
        },
      );
    }

    cancelPending() {
      if (this.pending) { this.pending.abort(); this.pending = null; }
    }

    /** One status line, announced politely, reused rather than recreated. */
    statusNode() {
      let el = this.root.querySelector('[data-status]');
      if (!el) {
        el = document.createElement('p');
        el.className = 'rv__busy';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        el.setAttribute('data-status', '');
        this.gridwrap.after(el);
      }
      return el;
    }

    showLoading() {
      const el = this.statusNode();
      el.textContent = 'Loading reviews…';
      el.hidden = false;
      // If the grid is currently an error panel there is nothing to keep
      // looking at, so the panel becomes the loading state rather than
      // sitting there contradicting the status line.
      if (this.loadError && !this.cardOrder.length) {
        this.grid.classList.remove('grid--masonry');
        this.grid.innerHTML = '<p class="rv__busy">Loading reviews…</p>';
      }
      if (this.moreBtn) this.moreBtn.disabled = true;
    }

    hideLoading() {
      const el = this.root.querySelector('[data-status]');
      if (el) { el.textContent = ''; el.hidden = true; }
      if (this.moreBtn) this.moreBtn.disabled = false;
    }

    /**
     * Either keep results we can still stand behind, or show nothing but the
     * failure. What we never do is show reviews that do not match the chip
     * the reader has selected.
     */
    showError() {
      this.loadError = { topic: this.topic ? this.topic.key : null };
      this.hideLoading();

      if (this.all) {
        // We can still honour the selection from what we hold, so the results
        // stay and only the refresh is reported as having failed.
        this.paint();
        this.renderNotice();
        return;
      }
      this.renderErrorPanel();
    }

    renderErrorPanel() {
      this.grid.classList.remove('grid--masonry');
      this.grid.innerHTML = `
        <div class="rv__err" role="alert">
          <p class="rv__err-msg">We couldn't load these reviews. Please try again.</p>
          <button class="btn btn--ghost rv__err-retry" type="button" data-retry>Retry</button>
        </div>`;
      this.cardOrder = [];
      this.gridwrap.classList.remove('is-clamped');
      this.gridwrap.style.maxHeight = '';
      this.hasMore = false;
      const wrap = this.moreBtn && this.moreBtn.closest('.more');
      if (wrap) wrap.hidden = true;
    }

    /** The softer case: results are real, refreshing them was not possible. */
    renderNotice() {
      if (this.root.querySelector('[data-notice]')) return;
      const el = document.createElement('div');
      el.className = 'rv__err rv__err--soft';
      el.setAttribute('role', 'alert');
      el.setAttribute('data-notice', '');
      el.innerHTML = `
        <p class="rv__err-msg">We couldn't load these reviews. Please try again.</p>
        <button class="btn btn--ghost rv__err-retry" type="button" data-retry>Retry</button>`;
      this.gridwrap.after(el);
    }

    clearError() {
      this.loadError = null;
      this.hideLoading();
      const n = this.root.querySelector('[data-notice]');
      if (n) n.remove();
    }

    /**
     * Retry the selection exactly as it stands. Nothing about the topic is
     * re-derived, so the pattern and rating floor are the ones the count was
     * taken with.
     */
    retry() {
      const hadFocus = this.root.contains(document.activeElement)
        && document.activeElement.hasAttribute('data-retry');
      const p = this.rerender();
      // The Retry button is about to be replaced. Leaving focus on a removed
      // node drops it to the body, so it goes back to the chip the reader
      // selected, which is where they were.
      if (hadFocus) {
        Promise.resolve(p).then(() => {
          const chip = this.root.querySelector('[data-topic][aria-pressed="true"]')
            || this.root.querySelector('[data-retry]');
          if (chip && chip.isConnected) chip.focus({ preventScroll: true });
        });
      }
      return p;
    }

    /**
     * Put the server-rendered cards back.
     *
     * Kept as a snapshot so clearing a filter is always local. Restoring from
     * the network would mean clearing a filter could fail, which is the one
     * state that has no honest thing to show.
     */
    restoreServerCards() {
      if (!this.serverCards || !this.serverCards.length) return;
      this.cardOrder = this.serverCards.slice();
      this.grid.classList.remove('grid--masonry');
      this.grid.replaceChildren(...this.cardOrder);
      this.shown = this.initialShown();
      this.root.dataset.shown = String(this.shown);
      this.expanded = false;
      this.layout();
      this.syncMore(this.total);
      this.applyPeek();
    }

    /** The filtered, sorted list the grid is currently showing a prefix of. */
    currentList() {
      let list = [...this.all];

      // Matched against title and body only, exactly as the server counted it,
      // and held to the same rating floor the thumbs up stands for.
      if (this.topic) {
        list = list.filter(r =>
          Number(r.star_rating) >= this.topic.min
          && this.topic.re.test(`${r.review_title || ''} ${r.review_body || ''}`));
      }

      if (this.filter === 'photos') list = list.filter(r => (r.media || []).length);
      else if (/^[1-5]$/.test(this.filter)) list = list.filter(r => Math.round(Number(r.star_rating)) === Number(this.filter));

      if (this.query) {
        list = list.filter(r =>
          `${r.review_title || ''} ${r.review_body || ''} ${r.reviewer_name || ''}`.toLowerCase().includes(this.query));
      }

      const byDate = (a, b) => new Date(b.review_date || 0) - new Date(a.review_date || 0);
      if (this.sort === 'highest') list.sort((a, b) => Number(b.star_rating) - Number(a.star_rating) || byDate(a, b));
      else if (this.sort === 'lowest') list.sort((a, b) => Number(a.star_rating) - Number(b.star_rating) || byDate(a, b));
      else if (this.sort === 'photos') list.sort((a, b) => (b.media || []).length - (a.media || []).length || byDate(a, b));
      else list.sort(byDate);
      return list;
    }

    /** Full rebuild. Only for a change of filter, sort or search. */
    paint() {
      const list = this.currentList();
      const visible = list.slice(0, this.shown);
      this.grid.innerHTML = visible.length
        ? visible.map(r => this.card(r)).join('')
        : '<p class="rv__busy">No reviews match that.</p>';
      this.gridwrap.classList.remove('is-clamped');
      this.gridwrap.style.maxHeight = '';
      this.root.dataset.shown = String(visible.length);
      this.captureOrder();
      this.layout();
      this.syncMore(list.length);
      this.applyPeek();
    }

    /**
     * Load more must not disturb what is already on screen. Rebuilding the
     * grid destroyed and recreated every card, and the round-robin deal then
     * put them all in new positions, so the review someone was half way
     * through jumped elsewhere on the page and they had to go hunting for it.
     * This renders only the new reviews and appends them to the columns,
     * continuing the deal where it left off. Nothing already rendered moves.
     */
    appendMore(list) {
      const have = this.cardOrder.length;
      const want = Math.min(this.shown, list.length);
      if (want > have) {
        const holder = document.createElement('div');
        holder.innerHTML = list.slice(have, want).map(r => this.card(r)).join('');
        const fresh = [...holder.children];
        this.cardOrder.push(...fresh);
        this.appendToColumns(fresh, have);
      }
      this.root.dataset.shown = String(this.cardOrder.length);
      this.syncMore(list.length);
      this.applyPeek();
    }

    appendToColumns(fresh, startIndex) {
      const cols = this.columnCount();
      const buckets = [...this.grid.querySelectorAll('.grid__col')];
      if (cols > 1 && buckets.length === cols) {
        fresh.forEach((c, i) => buckets[(startIndex + i) % cols].appendChild(c));
        return;
      }
      if (cols === 1 && !this.grid.classList.contains('grid--masonry')) {
        for (const c of fresh) this.grid.appendChild(c);
        return;
      }
      this.layout();   // the column count changed under us; a re-deal is unavoidable
    }

    card(r) {
      const media = r.media || [];
      const rating = Math.round(Number(r.star_rating) || 0);
      const body = String(r.review_body || '');
      return `<article class="rc" id="r${esc(r.review_id)}" data-review="${esc(r.review_id)}"
        data-rating="${rating}" data-photos="${media.length}">
        <span class="stars" style="--star-rating:${rating};" role="img" aria-label="${rating} out of 5 stars"></span>
        <div class="rc__who">
          <span class="rc__name">${esc(shortName(r.reviewer_name))}</span>
          ${r.verified_buyer ? `<span class="vb">${SHIELD}Verified buyer</span>` : ''}
        </div>
        <div class="rc__meta">${r.location ? esc(r.location) + ' &middot; ' : ''}${esc(fmtDate(r.review_date))}</div>
        ${r.review_title ? `<h3 class="rc__title heading-font">${esc(r.review_title)}</h3>` : ''}
        <p class="rc__body${body.length > 280 ? ' rc__body--clamp' : ''}" data-body>${esc(body)}</p>
        ${body.length > 280 ? `<button class="linkish" type="button" data-full="${esc(r.review_id)}">Read full review</button>` : ''}
        ${media.length ? `<div class="rm${media.length === 1 ? ' rm--one' : ''}">${
          media.map((m, i) => `<button class="rm__i${m.kind === 'video' ? ' rm__i--video' : ''}" type="button"
            data-open="${esc(r.review_id)}" data-idx="${i}"
            ${m.kind === 'video' ? `data-video="${esc(m.url)}"` : ''}
            aria-label="${m.kind === 'video' ? 'Play customer video' : `Open photo ${i + 1} of ${media.length}`}">
            <img src="${esc(m.thumb_url || m.url)}" data-fullsrc="${esc(m.url)}"
                 alt="${esc(mediaAlt(this.productTitle, r.reviewer_name, m.kind))}"
                 loading="lazy" decoding="async">
            ${m.kind === 'video' ? '<span class="rm__play" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18"><path d="M8 5v14l11-7z" fill="currentColor"/></svg></span>' : ''}
          </button>`).join('')}</div>` : ''}
        ${r.reply ? `<div class="reply"><p class="reply__hd heading-font">Daftboy replied</p><p>${esc(r.reply.body)}</p></div>` : ''}
        ${r.helpful_votes ? `<div class="rc__foot"><span class="rc__q">${r.helpful_votes} found this helpful</span></div>` : ''}
      </article>`;
    }

    /** Swaps a clamped preview for the full stored text. */
    /**
     * Most clamped cards already hold their whole text and just need the
     * clamp lifted, which is instant. Only the ones the payload actually
     * truncated need a round trip, and those are marked.
     */
    expand(btn) {
      const card = btn.closest('.rc');
      const body = card.querySelector('[data-body]');
      if (!btn.dataset.cut) {
        if (body) body.classList.remove('rc__body--clamp');
        btn.remove();
        this.layout();
        this.applyPeek();
        return;
      }
      const id = btn.dataset.full;
      return this.withBusy(async () => {
        await this.ensureAll();
        const full = this.all.find(r => String(r.review_id) === String(id));
        if (full && body) body.textContent = full.review_body || body.textContent;
        if (body) body.classList.remove('rc__body--clamp');
        btn.remove();
        this.layout();
        this.applyPeek();
      });
    }

    // ── photo wall ────────────────────────────────────────────────────────

    /**
     * Show the arrows only when there is somewhere for them to go.
     *
     * The strip renders from a single photo now, and a lone tile with a prev
     * and next button reads as broken rather than minimal. Re-measured on
     * resize and after load more, since both change whether the row overflows.
     */
    syncWallNav() {
      const wrap = this.root.querySelector('.pwrap');
      if (!wrap || !this.wall) return;
      wrap.classList.toggle('pwrap--nav', this.wall.scrollWidth > this.wall.clientWidth + 1);
    }

    scrollWall(dir) {
      if (!this.wall) return;
      this.wall.scrollBy({ left: dir * Math.max(220, this.wall.clientWidth * 0.8), behavior: 'smooth' });
    }

    /**
     * Clicking a tile opens the photo AND the review it came from.
     *
     * It used to pass `null` for the review, which is why the strip opened a
     * bare image. Every tile on the strip is a different customer, so the
     * review travels per shot rather than as one meta for the whole set: the
     * arrows walk the strip, and the person changes with the photo.
     *
     * The index comes from the tile's position in the DOM, not from its
     * data attribute, so tiles appended by "load more" are addressed correctly.
     */
    openWall(tile) {
      const shots = [];
      let index = 0;
      for (const t of this.root.querySelectorAll('[data-wall-open]')) {
        const img = t.querySelector('img');
        const url = img && (img.dataset.fullsrc || img.currentSrc || img.src);
        if (!url) continue;
        if (t === tile) index = shots.length;
        shots.push({
          url,
          thumb: img.dataset.fullsrc ? (img.currentSrc || img.src) : '',
          alt: img.alt || '',
          // A video tile carries its processed MP4; the still is its poster.
          video: t.dataset.video || null,
          meta: metaFromTile(t),
        });
      }
      if (!shots.length) return;
      this.showLightbox(shots, index, null);
    }

    /**
     * The end tile appends the next batch of tiles into the strip.
     *
     * It used to open a lightbox over every photo on the product, which
     * answered a question nobody asked: the tile sits at the end of a
     * carousel, so extending the carousel is the thing it should do.
     *
     * The endpoint ranks through the same builder that produced the metafield,
     * so tile 13 genuinely follows tile 12 instead of restarting the sequence
     * under a different sort. Its own busy state lives on the button, not in
     * the review grid's "Loading reviews" note, which sits far below the strip
     * and would be describing the wrong thing.
     */
    async loadMoreWall(btn) {
      if (btn.dataset.busy) return;
      btn.dataset.busy = '1';
      btn.disabled = true;
      try {
        const offset = Number(btn.dataset.wallOffset)
          || this.root.querySelectorAll('[data-wall-open]').length;
        const res = await fetch(
          `${this.endpoint}/photos?product_id=${encodeURIComponent(this.productId)}` +
          `&offset=${offset}&limit=${WALL_STEP}`, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`photos ${res.status}`);
        const data = await res.json();
        const tiles = Array.isArray(data.photos) ? data.photos : [];
        if (!tiles.length) { btn.remove(); return; }

        btn.insertAdjacentHTML('beforebegin',
          tiles.map((p, i) => wallTileHTML(p, offset + i, this.productTitle)).join(''));

        const shown = offset + tiles.length;
        if (!data.more) {
          btn.remove();
        } else {
          btn.dataset.wallOffset = String(shown);
          const peopleLeft = Math.max(0, (Number(data.people) || 0) - shown);
          const photosLeft = Math.max(0, (Number(data.photo_total) || 0) - shown);
          // Customers while there are still people nobody has seen; photos once
          // only second and third shots from those already shown remain.
          btn.innerHTML = peopleLeft > 0
            ? `<span>+${peopleLeft}</span><small>customers</small>`
            : `<span>+${photosLeft}</span><small>photos</small>`;
        }
        // Move the strip along so the new tiles are visibly there.
        this.syncWallNav();
        this.scrollWall(1);
      } catch (err) {
        console.warn('[reviews] photo strip load more failed:', err.message);
        btn.innerHTML = '<span>Retry</span><small>tap again</small>';
      } finally {
        delete btn.dataset.busy;
        btn.disabled = false;
      }
    }

    stepLightbox(dir) {
      if (!this.lbShots || this.lbShots.length < 2) return;
      this.lbIndex = (this.lbIndex + dir + this.lbShots.length) % this.lbShots.length;
      this.paintLightbox();
    }

    async openReview(id, index) {
      const card = this.root.querySelector(`[data-review="${CSS.escape(id)}"]`);
      if (!card) return;
      const shots = [...card.querySelectorAll('.rm__i img')].map(img => ({
        url: img.dataset.fullsrc || img.currentSrc || img.src, alt: img.alt,
        thumb: img.dataset.fullsrc ? (img.currentSrc || img.src) : '',
      }));
      const meta = {
        name: card.querySelector('.rc__name')?.textContent || '',
        title: card.querySelector('.rc__title')?.textContent || '',
        body: card.querySelector('[data-body]')?.textContent || '',
        rating: card.dataset.rating,
        when: card.querySelector('.rc__meta')?.textContent || '',
        verified: !!card.querySelector('.vb'),
      };
      this.showLightbox(shots, index, meta);
    }

    showLightbox(shots, index, meta) {
      this.lbIndex = Math.max(0, Math.min(index, shots.length - 1));
      this.lbShots = shots;
      this.lbMeta = meta;
      this.lbOpener = document.activeElement;
      this.paintLightbox();
      this.lb.hidden = false;
      this.lb.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      this.lb.querySelector('[data-lb-panel]').focus({ preventScroll: true });
    }

    paintLightbox() {
      const shot = this.lbShots[this.lbIndex];
      // A shot may carry its own review. The strip's tiles each belong to a
      // different customer, so the panel has to change with the photo; a
      // review's own photos still share the one meta passed to showLightbox.
      const m = ('meta' in shot) ? shot.meta : this.lbMeta;
      const thumbs = this.lbShots.length > 1 && this.lbShots.length <= 12
        ? `<div class="rv-lb__thumbs">${this.lbShots.map((s, i) =>
            `<button type="button" data-lb-pick="${i}" aria-current="${i === this.lbIndex}" aria-label="Photo ${i + 1}">
               <img src="${esc(s.url)}" alt=""></button>`).join('')}</div>`
        : '';
      const many = this.lbShots.length > 1;
      const nav = many ? `
        <button class="rv-lb__nav rv-lb__nav--prev" type="button" data-lb-prev aria-label="Previous photo">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <button class="rv-lb__nav rv-lb__nav--next" type="button" data-lb-next aria-label="Next photo">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <span class="rv-lb__count" role="status">${this.lbIndex + 1} of ${this.lbShots.length}</span>` : '';

      // Without review text there is nothing for a side panel to hold, so the
      // panel is dropped entirely rather than left as dead white space.
      this.lbPanel.className = `rv-lb__p${m ? '' : ' rv-lb__p--photo'}`;
      this.lbPanel.innerHTML = `
        <div class="rv-lb__img">${nav}${shot.video
          ? `<video class="rv-lb__video" controls playsinline preload="metadata"
                    poster="${esc(shot.thumb || '')}" src="${esc(shot.video)}"></video>`
          : `<img src="${esc(shot.thumb || shot.url)}" alt="${esc(shot.alt || '')}" data-lb-img>`}</div>
        ${m ? `<div class="rv-lb__side">
          <span class="stars" style="--star-rating:${esc(m.rating)};" role="img" aria-label="${esc(m.rating)} out of 5 stars"></span>
          <div class="rv-lb__who"><span>${esc(m.name)}</span>${m.verified ? `<span class="vb">${SHIELD}Verified buyer</span>` : ''}</div>
          <div class="rv-lb__meta">${esc(m.when)}</div>
          ${m.title ? `<h3 class="heading-font">${esc(m.title)}</h3>` : ''}
          <p>${esc(m.body)}</p>
          ${thumbs}
        </div>` : ''}`;
      /**
       * The originals are up to 2.8 MB, so pointing the lightbox straight at
       * one left the panel visibly empty for ~3s. The wall tile's thumbnail is
       * already decoded in cache, so it paints on the same frame the lightbox
       * opens and the original swaps itself in behind it.
       */
      const el = this.lbPanel.querySelector('[data-lb-img]');
      // A poster is not a thumbnail of the MP4, so there is nothing to upgrade.
      if (!shot.video && el && shot.thumb && shot.thumb !== shot.url) {
        el.classList.add('is-loading');
        const full = new Image();
        const token = ++this.lbToken;
        full.onload = () => {
          if (token !== this.lbToken || !el.isConnected) return;
          el.src = shot.url;
          el.classList.remove('is-loading');
        };
        full.src = shot.url;
      }

      for (const b of this.lbPanel.querySelectorAll('[data-lb-pick]')) {
        b.addEventListener('click', () => { this.lbIndex = Number(b.dataset.lbPick); this.paintLightbox(); });
      }
    }

    closeLightbox() {
      this.lb.classList.remove('is-open');
      this.lb.hidden = true;
      document.body.classList.remove('overflow-hidden');
      if (this.lbOpener && document.contains(this.lbOpener)) this.lbOpener.focus({ preventScroll: true });
    }
  }

  /**
   * One storewide card, matching sections/home-review-wall.liquid exactly.
   *
   * The server renders the first page and this renders every page after it,
   * so the two have to agree character for character or the grid changes
   * appearance halfway down. Same classes, same order, same clamp rule.
   */
  function homeCardHTML(rv) {
    const clamp = rv.cut || String(rv.b || '').length > 280;
    const meta = [];
    if (rv.loc) meta.push(esc(rv.loc));
    if (rv.d) meta.push(`<time datetime="${esc(rv.d)}">${fmtDate(rv.d)}</time>`);
    return `<article class="rc" id="home${esc(rv.id)}" data-review="${esc(rv.id)}" data-rating="${esc(rv.r)}">
      <span class="stars" style="--star-rating:${esc(rv.r)};" role="img" aria-label="${esc(rv.r)} out of 5 stars"></span>
      <div class="rc__who"><span class="rc__name">${esc(rv.a)}</span>${rv.vb ? `<span class="vb">${SHIELD}Verified buyer</span>` : ''}</div>
      <a class="rc__prodrow" href="/products/${esc(rv.ph)}">
        ${rv.pi ? `<img class="rc__thumb" src="${esc(rv.pi)}" width="112" height="112" alt="" loading="lazy" decoding="async">` : ''}
        <span class="rc__prod rc__prod--link">${esc(rv.pt)}</span>
      </a>
      ${meta.length ? `<div class="rc__meta">${meta.join(' &middot; ')}</div>` : ''}
      ${rv.t ? `<h4 class="rc__title heading-font">${esc(rv.t)}</h4>` : ''}
      <p class="rc__body${clamp ? ' rc__body--clamp' : ''}">${esc(rv.b)}${rv.cut ? '&hellip;' : ''}</p>
      ${clamp ? `<a class="linkish linkish--link" href="/products/${esc(rv.ph)}">Read full review</a>` : ''}
      ${rv.m ? `<div class="rm rm--one"><span class="rm__i"><img src="${esc(rv.m.t || rv.m.u)}" loading="lazy" decoding="async" alt="Customer review photo of ${esc(rv.pt)}${rv.a ? ` by ${esc(rv.a)}` : ''}"></span></div>` : ''}
    </article>`;
  }

  /**
   * The cross-product rollup grid.
   *
   * Two things it did badly. It was a plain CSS grid, where every row is as
   * tall as the tallest card in it, so one two-line review beside two long
   * ones left a band of white underneath. And it showed its whole pool at
   * once, which used to be nine cards and was all a family had.
   *
   * It now behaves like a product's own reviews: cards dealt round robin into
   * flex columns so each column packs tight while reading across a row still
   * gives 1, 2, 3; twelve shown; the rest behind the fade with a button that
   * says how many are left.
   *
   * Every card is already in the HTML, so expanding reveals rather than
   * fetches. There is no family endpoint to page against and no need for one.
   * With no JavaScript the markup stays the CSS grid it was rendered as, the
   * button stays hidden, and the CSS max-height keeps the fade honest.
   */
  class RollupGrid {
    constructor(wrap) {
      this.wrap = wrap;
      this.grid = wrap.querySelector('[data-roll-grid]');
      if (!this.grid) return;
      // Captured once. Re-reading after a layout would read the column-major
      // order back and shuffle the cards on the next resize.
      this.cards = [...this.grid.querySelectorAll('.rc')];
      this.total = this.cards.length;
      this.step = 12;
      this.shown = Math.min(this.initialShown(), this.total);
      this.cols = 0;

      const scope = wrap.parentElement || document;

      // Family chips. They filter the cards below them, which is the whole
      // reason the count on a chip is allowed to be printed: the number is a
      // promise that clicking shows that many cards.
      this.pool = this.cards;                 // what is being paged through
      this.chipWrap = scope.querySelector('[data-roll-topics]');
      if (this.chipWrap) {
        this.chipWrap.hidden = false;
        for (const b of this.chipWrap.querySelectorAll('[data-roll-topic]')) {
          b.addEventListener('click', () => this.toggleTopic(b));
        }
      }

      // Paging past what the metafield carries.
      //
      // The homepage wall holds the newest thirty six cards in its metafield
      // and there are 1975 published reviews, which is roughly a megabyte
      // against a 64KB ceiling. So once the local set runs out it asks the
      // app for the next page, the way a product page already does.
      //
      // Both are optional: without them this behaves exactly as before and
      // stops at the end of what was rendered, so a missing or failing
      // endpoint costs the extra pages and nothing else.
      this.endpoint = (wrap.dataset.rollEndpoint || '').replace(/\/+$/, '');
      this.grandTotal = Number(wrap.dataset.rollGrandTotal) || 0;
      this.fetching = false;
      this.exhausted = false;

      this.moreWrap = scope.querySelector('[data-roll-more-wrap]');
      this.moreBtn = this.moreWrap && this.moreWrap.querySelector('[data-roll-more]');
      this.moreLabel = this.moreWrap && this.moreWrap.querySelector('[data-roll-more-label]');
      if (this.moreBtn) this.moreBtn.addEventListener('click', () => this.expand());

      this.render();

      let t;
      window.addEventListener('resize', () => {
        clearTimeout(t);
        t = setTimeout(() => {
          // A narrower screen shows fewer to begin with, but never take away
          // cards the reader has already asked for.
          this.shown = Math.max(this.shown, Math.min(this.initialShown(), this.pool.length));
          this.render(true);
        }, 120);
      });
    }

    /**
     * Narrow to one topic, or clear it.
     *
     * Matched the same way the count was produced: the chip carries the very
     * pattern and the rating floor the counting used, so the number printed on
     * it and the cards revealed by it cannot drift apart.
     */
    toggleTopic(btn) {
      const on = btn.getAttribute('aria-pressed') === 'true';
      for (const b of this.chipWrap.querySelectorAll('[data-roll-topic]')) {
        b.setAttribute('aria-pressed', 'false');
      }
      if (on) {
        this.pool = this.cards;
      } else {
        btn.setAttribute('aria-pressed', 'true');
        let re = null;
        try { re = new RegExp(btn.dataset.topicRe, 'i'); } catch { re = null; }
        const min = Number(btn.dataset.topicMin) || 4;
        this.pool = !re ? this.cards : this.cards.filter((c) => {
          if (Number(c.dataset.rating) < min) return false;
          const t = c.querySelector('.rc__title');
          const b2 = c.querySelector('.rc__body');
          return re.test(`${t ? t.textContent : ''} ${b2 ? b2.textContent : ''}`);
        });
      }
      this.shown = Math.min(this.initialShown(), this.pool.length);
      this.render(true);
    }

    /** Matches the stylesheet and ReviewSection.columnCount. */
    columnCount() {
      const w = window.innerWidth;
      return w <= 860 ? 1 : w <= 1100 ? 2 : 3;
    }

    /** Five on one column, as agreed for the main list on mobile. */
    initialShown() { return this.columnCount() === 1 ? 5 : 12; }

    render(force = false) {
      const cols = this.columnCount();
      if (force || cols !== this.cols) { this.cols = cols; this.deal(); }
      else this.deal();
      this.clamp();
      this.syncMore();
    }

    deal() {
      const visible = this.pool.slice(0, this.shown);
      if (this.cols === 1) {
        this.grid.classList.remove('grid--masonry');
        this.grid.replaceChildren(...visible);
        return;
      }
      const buckets = Array.from({ length: this.cols }, () => {
        const d = document.createElement('div');
        d.className = 'grid__col';
        return d;
      });
      visible.forEach((card, i) => buckets[i % this.cols].appendChild(card));
      this.grid.classList.add('grid--masonry');
      this.grid.replaceChildren(...buckets);
    }

    /**
     * Clamp at the SHORTEST column, exactly as the main grid does.
     *
     * Columns finish at different heights, so cutting at the tallest would
     * leave white under the short ones and cutting per column is not a thing
     * a single container can do. Cutting every column on the shortest one's
     * line puts the gradient over one straight edge.
     */
    clamp() {
      if (this.shown >= this.pool.length && !this.hasRemote()) {
        this.wrap.classList.remove('is-clamped');
        this.wrap.style.maxHeight = '';
        return;
      }
      this.wrap.classList.add('is-clamped');
      const top = this.wrap.getBoundingClientRect().top;
      const cols = [...this.wrap.querySelectorAll('.grid__col')];
      let cut;
      if (cols.length > 1) {
        cut = Math.min(...cols.map(c => c.getBoundingClientRect().bottom - top));
      } else {
        const last = this.grid.querySelectorAll('.rc')[this.shown - 1];
        cut = last ? last.getBoundingClientRect().bottom - top : 0;
      }
      this.wrap.style.maxHeight = `${Math.round(Math.max(360, cut))}px`;
    }

    syncMore() {
      if (!this.moreWrap) return;
      const localLeft = this.pool.length - this.shown;
      // What is left counts the pages still on the server, so the button does
      // not announce "12 more" on the last local page and then keep going.
      const remoteLeft = this.hasRemote() ? this.grandTotal - this.cards.length : 0;
      const left = localLeft + remoteLeft;
      if (left <= 0) { this.moreWrap.hidden = true; return; }
      this.moreWrap.hidden = false;
      if (this.moreLabel) this.moreLabel.textContent = 'Load more reviews';
    }

    /**
     * Expand without moving what is being read.
     *
     * Cards are dealt round robin, so card i is always in column i % cols:
     * adding more appends below what is already there and nothing above the
     * reader shifts. The button itself moves down, which is the point.
     */
    expand() {
      if (this.shown < this.pool.length) {
        this.shown = Math.min(this.shown + this.step, this.pool.length);
        this.render(true);
        return;
      }
      this.fetchMore();
    }

    /** True while there are pages on the server this grid has not asked for. */
    hasRemote() {
      return Boolean(this.endpoint) && !this.exhausted && !this.filtered
        && this.cards.length < this.grandTotal;
    }

    async fetchMore() {
      if (!this.hasRemote() || this.fetching) return;
      this.fetching = true;
      if (this.moreBtn) this.moreBtn.disabled = true;
      try {
        const res = await fetch(
          `${this.endpoint}/reviews/all?limit=${this.step}&offset=${this.cards.length}`,
          { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        const incoming = Array.isArray(data.reviews) ? data.reviews : [];
        if (!incoming.length) { this.exhausted = true; return; }

        // Build the same card the server renders, so a fetched row and a
        // rendered one are indistinguishable once they are in the grid.
        const frag = [];
        for (const rv of incoming) {
          const el = document.createElement('div');
          el.innerHTML = homeCardHTML(rv);
          const card = el.firstElementChild;
          if (card) frag.push(card);
        }
        this.cards = this.cards.concat(frag);
        this.pool = this.cards;
        if (typeof data.total === 'number') this.grandTotal = data.total;
        this.shown = Math.min(this.shown + this.step, this.pool.length);
        this.render(true);
      } catch (err) {
        // Stop asking rather than retrying into a wall. What is already on
        // the page stays, and the button reflects what is left locally.
        this.exhausted = true;
        console.error('[reviews] could not load more', err);
      } finally {
        this.fetching = false;
        if (this.moreBtn) this.moreBtn.disabled = false;
        this.syncMore();
      }
    }
  }

  function initRollups(root = document) {
    for (const wrap of root.querySelectorAll('.rv [data-roll-wrap]')) {
      if (wrap.dataset.rollReady) continue;
      wrap.dataset.rollReady = '1';
      try {
        new RollupGrid(wrap);
      } catch (err) {
        console.error('[reviews] rollup grid failed to initialise', err);
      }
    }
  }

  /**
   * Retry a review image once when it fails to arrive.
   *
   * Review media is served by the app itself, with no CDN in front of it, so
   * every miss is a live request that can lose to congestion on a phone. When
   * one does, the browser leaves the tile blank for good: there is no second
   * attempt, and a reload will not make one either, because a failed response
   * can still be cached. One retry with a cache-busting parameter turns a
   * permanent hole in the strip back into a photo.
   *
   * Error events do not bubble, so this listens in the capture phase, and each
   * image is allowed exactly one retry so a genuinely missing file cannot turn
   * into a loop.
   */
  const RETRY_DELAY_MS = 900;
  function retryFailedMedia(e) {
    const img = e.target;
    if (!(img instanceof HTMLImageElement)) return;
    if (!img.closest('.rv, .hpw, .pwrap')) return;
    if (img.dataset.rvRetried) return;
    const src = img.getAttribute('src');
    if (!src) return;
    img.dataset.rvRetried = '1';
    setTimeout(() => {
      img.src = src + (src.includes('?') ? '&' : '?') + 'rv-retry=1';
    }, RETRY_DELAY_MS);
  }
  document.addEventListener('error', retryFailedMedia, true);

  function init(root = document) {
    // The rollup grid first, and on its own. It is independent of the review
    // section, and running it after meant a throw in that constructor took it
    // with it: exactly what happened on zero-review pages, which are the only
    // pages the rollup appears on at all.
    initRollups(root);

    for (const el of root.querySelectorAll('[data-review-section]')) {
      if (el.dataset.rvReady) continue;
      el.dataset.rvReady = '1';
      // One section failing must not stop the others, or anything after.
      try {
        new ReviewSection(el);
      } catch (err) {
        console.error('[reviews] section failed to initialise', err);
      }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
